/**
 * Single source of truth for the Leadboard navigation registry.
 * Drives sidebar, mobile drawer, mobile bottom bar, and command palette.
 *
 * Add a new tab here and it automatically appears everywhere.
 *
 * `role` — 'any' (visible to team_member + admin) | 'admin' (admin-only)
 * `badgeKey` — key in the `notificationCounts` object to display as a numeric badge
 * `soon` — true to render a "soon" pill (does not affect routing)
 */

import {
  Home,
  Users,
  Gift,
  Calendar,
  Share2,
  Clock,
  Coffee,
  Download,
  Target,
  Video,
  RefreshCw,
  Database,
  Mail,
  Sparkles,
  Search,
  Settings,
  Bell,
  CreditCard,
  Shield,
  Activity,
  HeartPulse,
  ShieldCheck,
} from 'lucide-react';

export const NAV = [
  {
    group: null,
    items: [
      { key: 'overview', label: 'Overview', Icon: Home, role: 'any' },
    ],
  },
  {
    group: 'Lead Sources',
    items: [
      { key: 'leads', label: 'Leads', Icon: Users, role: 'any', badgeKey: 'leads' },
      { key: 'holiday', label: 'Holiday', Icon: Gift, role: 'any' },
      { key: 'letstalktaxes', label: 'LTT', Icon: Calendar, role: 'any' },
      { key: 'referrals', label: 'Referrals', Icon: Share2, role: 'any' },
      { key: 'preregister', label: 'Pre-Reg', Icon: Clock, role: 'any' },
      { key: 'coffee', label: 'Coffee', Icon: Coffee, role: 'any' },
      { key: 'lttkgob', label: 'LTT-KGOB', Icon: Download, role: 'any' },
      { key: 'ppcads', label: 'PPC Ads', Icon: Target, role: 'any' },
      { key: 'healthcheck', label: 'Health Check', Icon: HeartPulse, role: 'any' },
      { key: 'penaltyrecovery', label: 'Penalty Recovery', Icon: ShieldCheck, role: 'any' },
      { key: 'consultations', label: 'Consult', Icon: Video, role: 'any' },
    ],
  },
  {
    group: 'System',
    items: [
      { key: 'crmsync', label: 'CRM Sync', Icon: RefreshCw, role: 'admin' },
      { key: 'bridge', label: 'Bridge DB', Icon: Database, role: 'admin' },
      { key: 'subscriptions', label: 'Subscriptions', Icon: Mail, role: 'admin' },
      { key: 'gya_queue', label: 'AI Queue', Icon: Sparkles, role: 'admin', badgeKey: 'gya_queue' },
      { key: 'indexing', label: 'Indexing', Icon: Search, role: 'admin', badgeKey: 'indexing' },
      { key: 'availability', label: 'Availability', Icon: Settings, role: 'admin' },
      { key: 'alerts', label: 'Alerts', Icon: Bell, role: 'admin', badgeKey: 'alerts' },
      { key: 'billing', label: 'Billing', Icon: CreditCard, role: 'admin' },
      { key: 'team', label: 'Team', Icon: Shield, role: 'admin' },
      { key: 'activity', label: 'Activity', Icon: Activity, role: 'admin' },
    ],
  },
];

// Flat lookup for label + role checks
export const TAB_MAP = (() => {
  const m = {};
  for (const sec of NAV) for (const it of sec.items) m[it.key] = it;
  return m;
})();

export function isTabAllowed(tabKey, role) {
  const it = TAB_MAP[tabKey];
  if (!it) return false;
  return it.role === 'any' || role === 'admin';
}

export function labelFor(tabKey) {
  return TAB_MAP[tabKey]?.label || tabKey;
}

// Mobile bottom bar (5 items max)
export const MOBILE_BOTTOM_ITEMS = ['overview', 'leads', 'ppcads', 'gya_queue'];

// Default tab fallback per role
export function defaultTabFor(role) {
  return role === 'admin' ? 'overview' : 'leads';
}
